import React, { Fragment, lazy, Suspense, useEffect, useState } from 'react';
import { lazyWithRetry } from "./utils/chunkLoadRecovery";
import { Router, Switch, Route, Redirect } from "react-router-dom";
import { riskCenterPath } from "./utils/riskCenterRoutes";
import { controlsPath } from "./utils/controlsRoutes";
import { auditPath } from "./utils/auditRoutes";
import { thirdPartyPath } from "./utils/thirdPartyRoutes";
import { marketplacePath } from "./utils/marketplaceRoutes";
import { surveyPath } from "./utils/surveyRoutes";
import { itPath } from "./utils/itRoutes";
import { profilePath, parseProfileLocation } from "./utils/profileRoutes";
import { manageRiskCenterPath, manageSubRiskCenterPath } from "./utils/manageRiskCenterRoutes";
import { controlPagePath } from "./utils/controlPageRoutes";
import { createBrowserHistory } from "history";
import { MobileNavProvider } from "./context/MobileNavContext";
import AuthenticatedShell from "./components/layout/AuthenticatedShell";

// Eagerly load auth pages — they're the first screen users hit
import Login from "./pages/auth/Login";
import Recovery from "./pages/auth/Recovery";

// Lazy-load all app pages — prefetched in the background so first-visit is instant
const Dashboards = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/dashboard/Dashboards")));
const Risk = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/risk/Risk")));
const Gantt = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/risk/Gantt")));
const Departments = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/organization/Departments")));
const Incidents = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/risk/Incidents")));
const Profile = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/account/Profile")));
const Settings = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/account/Settings")));
const Notifications = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/account/Notifications")));
const Help = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/account/Help")));
const Users = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/Users")));
const UserActivityPage = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/Admin/UserActivityPage")));
const Units = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/riskCenter/Units")));
const ManageRiskCenter = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/riskCenter/ManageRiskCenter")));
const ManageSubRiskCenter = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/riskCenter/ManageSubRiskCenter")));
const Files = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/files/Files")));
const RiskLevels = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/risk/RiskLevels")));
const Compliance = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/compliance/Compliance")));
const ComplianceCards = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/compliance/ComplianceCards")));
const BowtieAnalysis = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/risk/BowtieAnalysis")));
const IT = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/it/IT")));
const ThirdParty = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/thirdParty/ThirdParty")));
const VendorPortal = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/thirdParty/pages/VendorPortal")));
const WorkflowEditor = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./components/Workflow/WorkflowEditor")));
const AllControls = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/risk/AllControls")));
const ControlPage = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/controls/ControlPage")));
const RiskPage = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./components/Risk/RiskPage")));
const Survey = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/survey/Survey")));
const Audit = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/audit/Audit")));
const Marketplace = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/marketplace/Marketplace")));
const Chatbot = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/ai/Chatbot")));
const NotFound = lazy(() => lazyWithRetry(() => import(/* webpackPrefetch: true */ "./pages/errors/NotFound")));

const history = createBrowserHistory();

const API_URL =
  process.env.NODE_ENV === "production"
    ? "https://api.test.raborisk.ai"
    : "http://localhost:3000";

const getTenantSubdomain = () => {
  const parts = window.location.hostname.split(".");
  if (parts.length >= 3 && parts[0] !== "www") return parts[0];
  return null;
};

function useNormalizeTo100({ targetDPR = 1, userScale = 1 } = {}) {
  useEffect(() => {
    const apply = () => {
      const dpr = window.devicePixelRatio || 1;
      const uiScale = (targetDPR / dpr) * userScale;
      document.documentElement.style.setProperty("--ui-scale", String(uiScale));
    };
    apply();

    const onResize = () => apply();
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", apply);

    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", apply);
    };
  }, [targetDPR, userScale]);
}

const AppShell = () => (
  <div className="w-full h-screen flex flex-col bg-gray-50 overflow-hidden">
    <div className="flex-shrink-0 h-16 bg-white border-b border-gray-200" />
    <div className="flex flex-1 min-h-0 overflow-hidden">
      <div className="hidden md:flex flex-col w-16 h-full bg-white border-r border-gray-200 flex-shrink-0" />
      <div className="flex-1 bg-gray-50" />
    </div>
  </div>
);

export { AppShell };

function Main() {
  const [userScale] = useState(() => {
    const v = localStorage.getItem("uiScale");
    return v ? Number(v) : 1;
  });
  useEffect(() => { localStorage.setItem("uiScale", String(userScale)); }, [userScale]);

  useNormalizeTo100({ targetDPR: 1, userScale });

  const [tenantReady, setTenantReady] = useState(false);

  useEffect(() => {
    const isTestHost = window.location.hostname === "test.raborisk.ai" || window.location.hostname === "www.test.raborisk.ai";
    if (isTestHost) { setTenantReady(true); return; }
    const subdomain = getTenantSubdomain();
    if (!subdomain) { setTenantReady(true); return; }

    fetch(`${API_URL}/api/tenant/check`, {
      headers: { "x-tenant-subdomain": subdomain },
    })
      .then((res) => {
        if (res.status === 451) {
          if (isTestHost) {
            setTenantReady(true);
            return;
          }
          return res.json().then((data) => {
            window.location.replace(data.redirect || "https://www.finandfind.com/");
          });
        }
        setTenantReady(true);
      })
      .catch(() => setTenantReady(true));
  }, []);

  if (!tenantReady) return <AppShell />;

  return (
    <div className="App">
      <Fragment>
        <Switch>
          {/* Public routes — no persistent layout */}
          <Route exact path="/" component={Login} />
          <Route exact path="/login" component={Login} />
          <Route exact path="/recovery" component={Recovery} />
          <Route exact path="/vendor-portal" render={({ location }) => {
            const params = new URLSearchParams(location.search);
            return (
              <Suspense fallback={null}>
                <VendorPortal initialToken={params.get("token") || ""} isExternal onClose={() => {}} />
              </Suspense>
            );
          }} />

          {/* All authenticated routes share ONE layout instance */}
          <Route path="/">
            <AuthenticatedShell>
              <Switch>
                <Route exact path="/dashboards/:id?" component={Dashboards} />
                <Route exact path="/bowtie/:id" component={BowtieAnalysis} />
                <Route exact path="/risk/:view_risk?" component={Risk} />
                <Route exact path="/actions" component={Gantt} />
                <Route exact path="/departments" component={Departments} />
                <Route exact path="/incidents/:risk_id" component={Incidents} />
                <Route exact path="/profile" render={({ location }) => (
                  <Redirect to={profilePath(parseProfileLocation(location).tab)} />
                )} />
                <Route exact path="/profile/:tab" component={Profile} />
                <Route exact path="/settings" component={Settings} />
                <Route exact path="/notifications" component={Notifications} />
                <Route exact path="/help" component={Help} />
                <Route exact path="/admin/users/:userId/activity" component={UserActivityPage} />
                <Route exact path="/admin" component={Users} />

                {/* Risk Center — URL-driven tab navigation */}
                <Route exact path="/risk_center" render={() => <Redirect to={riskCenterPath('dashboard')} />} />
                <Route exact path="/risk-center" render={() => <Redirect to={riskCenterPath('dashboard')} />} />
                <Route exact path="/risk_center/compliance/:complianceId/:tab?" component={Units} />
                <Route exact path="/risk_center/:tab" component={Units} />

                <Route exact path="/controls" render={() => <Redirect to={controlsPath("dashboard")} />} />
                <Route exact path="/controls/:tab" component={AllControls} />
                <Route exact path="/control_overview/:id" render={({ match }) => (
                  <Redirect to={controlPagePath(match.params.id)} />
                )} />
                <Route exact path="/control_overview/:id/:tab" component={ControlPage} />
                <Route exact path="/survey" render={() => <Redirect to={surveyPath()} />} />
                <Route exact path="/survey/:tab" component={Survey} />
                <Route exact path="/audit" render={() => <Redirect to={auditPath("audits")} />} />
                <Route exact path="/audit/audits/:surveyId/:subTab" component={Audit} />
                <Route exact path="/audit/:tab" component={Audit} />
                <Route exact path="/files" component={Files} />
                <Route exact path="/risk_levels" component={RiskLevels} />
                <Route exact path="/risk_overview/:id" component={RiskPage} />
                <Route exact path="/compliance" component={Compliance} />
                <Route exact path="/standards" component={ComplianceCards} />
                <Route exact path="/IT" render={() => <Redirect to={itPath()} />} />
                <Route exact path="/IT/dashboards/:riskId" component={IT} />
                <Route exact path="/IT/:tab" component={IT} />
                <Route exact path="/third_party" render={() => <Redirect to={thirdPartyPath("overview")} />} />
                <Route exact path="/third_party/vendors/:vendorId/:detailTab" component={ThirdParty} />
                <Route exact path="/third_party/vendors/:vendorId" component={ThirdParty} />
                <Route exact path="/third_party/:tab" component={ThirdParty} />
                <Route exact path="/edit/:id" component={WorkflowEditor} />
                <Route exact path="/marketplace" render={() => <Redirect to={marketplacePath("templates")} />} />
                <Route exact path="/marketplace/:tab" component={Marketplace} />
                <Route exact path="/chat" component={Chatbot} />
                <Route exact path="/chat/:chatId" component={Chatbot} />
                <Route exact path="/manage_risk_center/:id" render={({ match }) => (
                  <Redirect to={manageRiskCenterPath(match.params.id)} />
                )} />
                <Route exact path="/manage_risk_center/:id/:tab" component={ManageRiskCenter} />
                <Route exact path="/manage_sub_risk_center/:id" render={({ match }) => (
                  <Redirect to={manageSubRiskCenterPath(match.params.id)} />
                )} />
                <Route exact path="/manage_sub_risk_center/:id/:tab" component={ManageSubRiskCenter} />
                <Route component={NotFound} />
              </Switch>
            </AuthenticatedShell>
          </Route>
        </Switch>
      </Fragment>
    </div>
  );
}

function App() {
  return (
    <Router history={history}>
      <MobileNavProvider>
        <Main />
      </MobileNavProvider>
    </Router>
  );
}

export default App;
